import { useEffect, useState } from "react";
import LobbyPreviewShell from "./LobbyPreviewShell";
import { ApiError, fetchLobbyPreviewSnapshot } from "./api";
import type { LobbyPreviewBundle } from "./lobbyPreviewBus";

// The cross-device lobby preview (ADR 0134 part B) at the PUBLIC /lobby-preview/:code
// — opened by scanning the editor's QR on a phone/tablet. It polls the code-authed
// snapshot the editor pushes (no account; the code is the only credential) and
// renders the shared shell. An unknown/expired code is a generic 404 → "link
// expired"; a live session with no push yet shows the waiting state.

const POLL_MS = 1500;

export default function LobbyDevicePreviewScreen({ code }: { code: string }) {
  const [bundle, setBundle] = useState<LobbyPreviewBundle | null>(null);
  const [connected, setConnected] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let active = true;
    const poll = () =>
      fetchLobbyPreviewSnapshot<LobbyPreviewBundle>(code)
        .then((b) => {
          if (!active) return;
          if (b) {
            setBundle(b);
            setConnected(true);
          } else {
            setConnected(false); // session live but the editor hasn't pushed yet
          }
        })
        .catch((e) => {
          if (!active) return;
          if (e instanceof ApiError && e.status === 404) setExpired(true);
          // transient errors: keep the last frame, try again next tick
        });
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [code]);

  const mode = typeof window !== "undefined" && window.innerWidth < 700 ? "phone" : "desktop";

  if (expired) {
    return (
      <main role="status" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-base)", padding: 24 }}>
        <div style={{ textAlign: "center", color: "var(--text-dim)" }}>
          <p style={{ margin: 0, fontSize: "var(--fs-lg)", color: "var(--text-hi)" }}>Preview link expired</p>
          <p style={{ margin: "8px 0 0", fontSize: "var(--fs-body-2)" }}>Open “Preview on my device” in the editor again for a fresh code.</p>
        </div>
      </main>
    );
  }

  return (
    <LobbyPreviewShell
      bundle={bundle}
      mode={mode}
      connected={connected}
      statusLabel="on this device"
      waitingNote="Keep the Designer open on your computer — this device updates as you edit."
    />
  );
}
